import { Delete } from '@mui/icons-material';
import {
  Alert,
  Box,
  FormHelperText,
  Grid,
  IconButton,
  styled,
} from '@mui/material';
import TransactionTypeSelector from 'components/new/TransactionTypeSelector';
import { TransactionOperation } from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import { useState } from 'react';
import { ReconcilableTransaction } from './useReconcilableTransactions';

const ButtonBox = styled(Box)({
  height: 41,
  display: 'flex',
  alignItems: 'center',
});

type Props = {
  index: number;
  transactionErrors?: {
    message?: string;
    transactionTypeId?: { message?: string };
  };
  transaction: {
    transactionTypeId: string;
    amountLcy: number;
    amountTcy: number;
    operationType: TransactionOperation;
    reconcilableTransaction?: ReconcilableTransaction;
  };
  vehicleId: string;
  currency: CurrencyCode;
  onTransctionTypeChange: (index: number, value: string) => void;
  onDebitAmountChange: (index: number, value: number) => void;
  onCreditAmountChange: (index: number, value: number) => void;
  onRemove: (index: number) => void;
  disableRemove?: boolean;
};

const Transaction: React.FC<Props> = ({
  index,
  transaction,
  currency,
  vehicleId,
  transactionErrors,
  onTransctionTypeChange,
  onDebitAmountChange,
  onCreditAmountChange,
  onRemove,
  disableRemove,
}) => {
  const [isNewPayableTransaction, setIsNewPayableTransaction] = useState(false);
  const [isNewReceivableTransaction, setIsNewReceivableTransaction] =
    useState(false);

  const showRemoveButton = index > 1 && !disableRemove;

  const debitAmountTcy =
    transaction.operationType === TransactionOperation.Debit
      ? transaction.amountTcy
      : 0;

  const creditAmountTcy =
    transaction.operationType === TransactionOperation.Credit
      ? transaction.amountTcy
      : 0;

  const isReconciliationTransaction =
    transaction.reconcilableTransaction &&
    transaction.reconcilableTransaction.operationType !==
      transaction.operationType;

  return (
    <div>
      <Column spacing="sm" fullWidth>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size="grow">
            <TransactionTypeSelector
              vehicleId={vehicleId}
              value={transaction.transactionTypeId}
              onChange={(value, accountDetails) => {
                onTransctionTypeChange(index, value);

                if (!transaction.reconcilableTransaction) {
                  setIsNewPayableTransaction(accountDetails.payable ?? false);
                  setIsNewReceivableTransaction(
                    accountDetails.receivable ?? false,
                  );
                }
              }}
              disabled={isReconciliationTransaction}
              withoutPayablesAndReceivables={
                !!transaction.reconcilableTransaction &&
                !isReconciliationTransaction
              }
              error={transactionErrors?.transactionTypeId}
            />
          </Grid>
          <Grid size={3}>
            <NumberInput
              fullWidth
              value={debitAmountTcy}
              onChange={(value) => {
                onDebitAmountChange(index, value ?? 0);
              }}
              currency={currency}
              decimals={2}
              fixedDecimalScale
              placeholder="Enter debit amount"
              clearZeroValueOnFocus
              disabled={
                !!creditAmountTcy || !!transaction.reconcilableTransaction
              }
              error={!!transactionErrors}
            />
          </Grid>
          <Grid size={3}>
            <NumberInput
              fullWidth
              value={creditAmountTcy}
              onChange={(value) => {
                onCreditAmountChange(index, value ?? 0);
              }}
              currency={currency}
              decimals={2}
              fixedDecimalScale
              placeholder="Enter credit amount"
              clearZeroValueOnFocus
              disabled={
                !!debitAmountTcy || !!transaction.reconcilableTransaction
              }
              error={!!transactionErrors}
            />
          </Grid>
          <Grid size={1}>
            {showRemoveButton ? (
              <ButtonBox>
                <IconButton
                  size="small"
                  onClick={() => {
                    onRemove(index);
                  }}
                >
                  <Delete />
                </IconButton>
              </ButtonBox>
            ) : null}
          </Grid>
        </Grid>
        {isNewPayableTransaction &&
        transaction.operationType === TransactionOperation.Credit ? (
          <Alert severity="info">
            This will create a payable that will be available for bank
            reconciliation.
          </Alert>
        ) : null}
        {isNewReceivableTransaction &&
        transaction.operationType === TransactionOperation.Debit ? (
          <Alert severity="info">
            This will create a receivable that will be available for bank
            reconciliation.
          </Alert>
        ) : null}
      </Column>
      {transactionErrors?.message ? (
        <FormHelperText error>{transactionErrors.message}</FormHelperText>
      ) : null}
    </div>
  );
};

export default Transaction;
